/**
 * ONE TAP — the one-gun-per-team weapon system.
 *
 * This module owns the rule the whole game is built on (DESIGN.md §1):
 *
 *   > A round is chambered ONLY by catching the gun cleanly in mid-air.
 *   > Picking a gun up off the ground gives you an EMPTY gun.
 *
 * Everything here protects that rule. Firing empties the chamber. The only way back to a
 * loaded gun is a throw that somebody *else* catches: THROW_SELF_CATCH_LOCKOUT_MS stops the
 * instant juggle, and catching a gun you threw yourself never chambers it no matter how
 * long you wait (see `doCatch`). The dry click of an empty gun is not a failure state to
 * be smoothed over — it is the single most important emotional beat in the game and it
 * fires reliably, every time, on the press edge.
 *
 * Runs identically on the Cloudflare Worker and in the browser: no `three`, no DOM,
 * no Math.random().
 *
 * ---------------------------------------------------------------------------
 * CALL CONTRACT (world.ts)
 *   Every tick, for every connected player, call in this order:
 *     stepMovement(p, cmd, ...)   // movement agent
 *     tryFire(p, cmd, w, world, events)
 *     tryThrow(p, cmd, w, events)
 *     tryPulse(p, cmd, w, events)
 *     tryPickup(p, w, events)
 *   then, once per tick:
 *     for (const g of w.guns) stepGun(g, w, world, events)
 *     resolveCatches(w, events)
 *
 *   tryFire / tryThrow / tryPulse must be called EVERY tick (not only when the button is
 *   down): they carry the button-edge detection and the throw charge timer. Calling them
 *   with the button released is how a charged throw releases and how the fire edge resets.
 * ---------------------------------------------------------------------------
 */

import {
  CATCH_CHAMBERS,
  CATCH_FIRE_DELAY_MS,
  CATCH_HEALS_WOUNDED,
  CATCH_MIN_HEIGHT,
  CATCH_RADIUS,
  CATCH_REQUIRE_LOS,
  CATCH_VIEW_CONE,
  FIRE_COOLDOWN_MS,
  GRAVITY,
  GUN_ABANDONED_RESET_MS,
  GUN_AIR_DRAG,
  GUN_BOUNCE_FRICTION,
  GUN_BOUNCE_RESTITUTION,
  GUN_GRAVITY_SCALE,
  GUN_RADIUS,
  GUN_REST_SPEED,
  GUN_VOID_RESPAWN_MS,
  INTERP_DELAY_MS,
  MAX_GROUND_SLOPE,
  MAX_REWIND_MS,
  PICKUP_FROM_GROUND_CHAMBERS,
  PLAYER_HEIGHT,
  PLAYER_CROUCH_HEIGHT,
  PLAYER_RADIUS,
  PULSE_ARMED_MULT,
  PULSE_CONE,
  PULSE_COOLDOWN_MS,
  PULSE_DISARMS,
  PULSE_GUN_IMPULSE,
  PULSE_PLAYER_IMPULSE,
  PULSE_RANGE,
  TEAM_A,
  TEAM_B,
  THROW_CHARGE_MS,
  THROW_INHERIT_VELOCITY,
  THROW_SELF_CATCH_LOCKOUT_MS,
  THROW_SPEED,
  THROW_SPEED_CHARGED,
  THROW_SPIN_RATE,
  THROW_UP_BIAS,
  TICK_DT,
  TICK_MS,
} from '@shared/constants';
import {
  Btn,
  GunState,
  HealthState,
  RoundPhase,
  SurfaceKind,
  hasBtn,
  type CollisionWorld,
  type GameEvent,
  type GunEntity,
  type InputCommand,
  type PlayerState,
  type RaycastResult,
  type TraceResult,
  type WorldState,
} from '@shared/types';
import {
  EPSILON,
  TAU,
  addScaled,
  clamp,
  clamp01,
  copy,
  dirFromYawPitch,
  dot,
  length,
  lengthSq,
  lerpN,
  normalize,
  rightFromYaw,
  set,
  v3,
  type Vec3,
} from '@shared/math';
import { applyDamage, reviveToFull, traceShot } from '@shared/sim/combat';
import { eyePosition } from '@shared/sim/movement';

// ---------------------------------------------------------------------------
// Solver constants (not gameplay tunables — numerical hygiene only)
// ---------------------------------------------------------------------------

/** Bounce resolution passes per tick. Enough for a corner; bounded so a wedge can't hang. */
const GUN_SOLVER_ITERATIONS = 4;
/** Keeps the swept sphere a hair off the surface so the next sweep never starts solid. */
const GUN_SKIN = 0.002;
/** Cosine of the steepest surface the gun will treat as "floor" for settling. */
const GUN_FLOOR_COS = Math.cos(MAX_GROUND_SLOPE);
/** Cosine of the pulse cone half-angle. */
const PULSE_CONE_COS = Math.cos(PULSE_CONE);
/** Cosine of the catch view cone half-angle. You can only catch what you are looking at. */
const CATCH_VIEW_COS = Math.cos(CATCH_VIEW_CONE);

// ---------------------------------------------------------------------------
// Per-tick bookkeeping that PlayerState has no field for.
//
// Both maps are keyed by the PlayerState object and are pure derived state: losing them
// (a fresh snapshot object on the client, say) costs at most one tick of edge detection.
// They are WeakMaps so a leaving player is collected for free.
// ---------------------------------------------------------------------------

interface BtnRecord {
  tick: number;
  cur: number;
  prev: number;
}

const btnRecords = new WeakMap<PlayerState, BtnRecord>();
const timerTicks = new WeakMap<PlayerState, number>();
/** Last CollisionWorld seen for a world, so `resolveCatches` can probe the floor. */
const worldsFor = new WeakMap<WorldState, CollisionWorld>();

function noteInput(p: PlayerState, cmd: InputCommand, w: WorldState): void {
  const r = btnRecords.get(p);
  if (!r) {
    btnRecords.set(p, { tick: w.tick, cur: cmd.buttons, prev: cmd.buttons });
    return;
  }
  if (r.tick !== w.tick) {
    // If ticks were skipped (dead, frozen in warmup, just joined) we have no honest
    // previous sample, so we refuse to synthesise an edge. Otherwise a player who simply
    // held the trigger through the countdown would be handed a free shot on the whistle.
    r.prev = w.tick - r.tick === 1 ? r.cur : cmd.buttons;
    r.tick = w.tick;
  }
  r.cur = cmd.buttons;
}

/** True only on the tick the button goes down. Semi-auto fire and one-shot pulse depend on this. */
function pressedEdge(p: PlayerState, b: Btn): boolean {
  const r = btnRecords.get(p);
  if (!r) return false;
  return (r.cur & b) !== 0 && (r.prev & b) === 0;
}

function heldNow(p: PlayerState, b: Btn): boolean {
  const r = btnRecords.get(p);
  return r ? (r.cur & b) !== 0 : false;
}

/**
 * Weapon timers advance exactly once per tick no matter how many gun entry points touch
 * this player, so call ordering in world.ts can never double- or half-speed a cooldown.
 */
function advanceWeaponTimers(p: PlayerState, w: WorldState): void {
  if (timerTicks.get(p) === w.tick) return;
  timerTicks.set(p, w.tick);
  if (p.fireCooldownMs > 0) p.fireCooldownMs = Math.max(0, p.fireCooldownMs - TICK_MS);
  if (p.catchLockoutMs > 0) p.catchLockoutMs = Math.max(0, p.catchLockoutMs - TICK_MS);
  if (p.pulseCooldownMs > 0) p.pulseCooldownMs = Math.max(0, p.pulseCooldownMs - TICK_MS);
}

/**
 * Offensive actions are frozen during the pre-round countdown and after the round is
 * decided. Lobby is deliberately left playable so a warm-up sandbox still works.
 */
function canAct(w: WorldState, p: PlayerState): boolean {
  if (p.healthState === HealthState.Dead) return false;
  const ph = w.match.phase;
  return ph === RoundPhase.Live || ph === RoundPhase.Lobby;
}

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

/** The gun this player is holding, or null. `heldGun` indexes `w.guns` but is validated by id. */
export function heldGunOf(p: PlayerState, w: WorldState): GunEntity | null {
  if (p.heldGun < 0) return null;
  let g: GunEntity | null = null;
  const direct = w.guns[p.heldGun];
  if (direct && direct.id === p.heldGun) g = direct;
  else {
    for (let i = 0; i < w.guns.length; i++) {
      const cand = w.guns[i];
      if (cand && cand.id === p.heldGun) {
        g = cand;
        break;
      }
    }
  }
  if (!g) return null;
  if (g.holder !== p.id) return null;
  if (g.state !== GunState.HeldChambered && g.state !== GunState.HeldEmpty) return null;
  return g;
}

function fairSpawn(g: GunEntity, world: CollisionWorld, out: Vec3): Vec3 {
  const a = world.arena;
  if (g.team === TEAM_A) return copy(out, a.gunSpawnA);
  if (g.team === TEAM_B) return copy(out, a.gunSpawnB);
  return copy(out, a.centerSpawn);
}

/** Detach a gun from its holder and launch it. Shared by throw, disarm and hand-off cases. */
function launch(g: GunEntity, from: PlayerState | null, vel: Vec3, lockoutMs: number): void {
  if (from) from.heldGun = -1;
  g.state = GunState.InFlight;
  g.holder = null;
  g.lastThrower = from ? from.id : null;
  g.throwLockoutMs = lockoutMs;
  g.idleMs = 0;
  g.airborneMs = 0;
  g.throwOrigin = { x: g.pos.x, y: g.pos.y, z: g.pos.z };
  copy(g.vel, vel);
}

// ---------------------------------------------------------------------------
// stepGun — thrown-gun physics
// ---------------------------------------------------------------------------

const _prev = v3();
const _end = v3();
const _tan = v3();
const _spawn = v3();
const _eye = v3();
const _fwd = v3();
const _tr: TraceResult = {
  fraction: 1,
  hit: false,
  normal: v3(),
  point: v3(),
  surfaceId: -1,
  surface: SurfaceKind.Concrete,
  startSolid: false,
};
const _down: Vec3 = { x: 0, y: -1, z: 0 };
const _probe: RaycastResult = {
  hit: false,
  distance: 0,
  point: v3(),
  normal: v3(),
  surfaceId: -1,
  surface: SurfaceKind.Concrete,
};

/**
 * One tick of gun physics. Held guns ride the holder; in-flight guns are a swept sphere
 * with gravity, drag, restitution and tangential friction; grounded guns just age toward
 * the abandoned-reset; voided guns count down to a fair respawn.
 */
export function stepGun(g: GunEntity, w: WorldState, world: CollisionWorld, events: GameEvent[]): void {
  worldsFor.set(w, world);

  switch (g.state) {
    case GunState.HeldChambered:
    case GunState.HeldEmpty:
      stepHeld(g, w);
      return;
    case GunState.InFlight:
      stepInFlight(g, w, world, events);
      return;
    case GunState.Grounded:
      stepGrounded(g, w, world, events);
      return;
    case GunState.Void:
      stepVoid(g, world, events);
      return;
    default:
      return;
  }
}

function stepHeld(g: GunEntity, w: WorldState): void {
  const holder = g.holder ? w.players[g.holder] : undefined;

  // Self-heal: a holder who vanished, died, or no longer points back at this gun must not
  // take it with them. Drop it where they stood rather than leaking it out of the round.
  if (!holder || holder.healthState === HealthState.Dead || holder.heldGun !== g.id) {
    // Only clear the back-reference if it actually points at *this* gun — a player whose
    // heldGun points somewhere else is holding a different gun and must not be stripped.
    if (holder && holder.heldGun === g.id) holder.heldGun = -1;
    g.state = GunState.InFlight;
    g.holder = null;
    g.throwLockoutMs = 0;
    g.idleMs = 0;
    g.airborneMs = 0;
    g.throwOrigin = { x: g.pos.x, y: g.pos.y, z: g.pos.z };
    set(g.vel, 0, 0, 0);
    return;
  }

  // Ride the holder. The sim position is what spatial audio (the loaded-gun hum, our most
  // important sound) and the PULSE range test read, so keep it out in front of the chest.
  eyePosition(holder, _eye);
  dirFromYawPitch(_fwd, holder.yaw, holder.pitch);
  addScaled(g.pos, _eye, _fwd, PLAYER_RADIUS);
  copy(g.vel, holder.vel);
  set(g.spin, 0, 0, 0);
  set(g.rot, holder.pitch, holder.yaw, 0);
  g.idleMs = 0;
  g.voidRespawnMs = 0;
  g.airborneMs = 0;
  g.throwOrigin = null;
  if (g.throwLockoutMs > 0) g.throwLockoutMs = Math.max(0, g.throwLockoutMs - TICK_MS);
  g.state = g.chambered ? GunState.HeldChambered : GunState.HeldEmpty;
}

function stepInFlight(g: GunEntity, w: WorldState, world: CollisionWorld, events: GameEvent[]): void {
  if (g.throwLockoutMs > 0) g.throwLockoutMs = Math.max(0, g.throwLockoutMs - TICK_MS);
  g.idleMs += TICK_MS;
  g.airborneMs += TICK_MS;

  // Integrate forces (semi-implicit Euler; drag is exponential so it is dt-independent).
  g.vel.y += GRAVITY * GUN_GRAVITY_SCALE * TICK_DT;
  const drag = Math.exp(-GUN_AIR_DRAG * TICK_DT);
  g.vel.x *= drag;
  g.vel.y *= drag;
  g.vel.z *= drag;

  let remaining = TICK_DT;
  let restedOnFloor = false;

  for (let iter = 0; iter < GUN_SOLVER_ITERATIONS && remaining > EPSILON; iter++) {
    if (lengthSq(g.vel) < EPSILON) break;

    copy(_prev, g.pos);
    addScaled(_end, g.pos, g.vel, remaining);
    const tr = world.traceSphere(_prev, _end, GUN_RADIUS, _tr);

    if (!tr.hit) {
      copy(g.pos, _end);
      remaining = 0;
      break;
    }

    if (tr.startSolid) {
      // Wedged inside geometry (spawned into a brush, or squeezed by a moving surface).
      // Settle in place instead of tunnelling — a gun must always be findable.
      set(g.vel, 0, 0, 0);
      restedOnFloor = true;
      break;
    }

    // Advance to the contact point, held a skin width off the surface.
    const f = clamp01(tr.fraction);
    g.pos.x = _prev.x + (_end.x - _prev.x) * f + tr.normal.x * GUN_SKIN;
    g.pos.y = _prev.y + (_end.y - _prev.y) * f + tr.normal.y * GUN_SKIN;
    g.pos.z = _prev.z + (_end.z - _prev.z) * f + tr.normal.z * GUN_SKIN;

    const vn = dot(g.vel, tr.normal);
    const impact = Math.abs(vn);
    if (impact > GUN_REST_SPEED) {
      events.push({
        t: 'gunbounce',
        gunId: g.id,
        pos: { x: g.pos.x, y: g.pos.y, z: g.pos.z },
        speed: impact,
        surface: tr.surface,
      });
    }

    // Split into normal (restitution) and tangential (friction) components.
    _tan.x = g.vel.x - tr.normal.x * vn;
    _tan.y = g.vel.y - tr.normal.y * vn;
    _tan.z = g.vel.z - tr.normal.z * vn;
    const keep = 1 - GUN_BOUNCE_FRICTION;
    const bounce = vn < 0 ? -vn * GUN_BOUNCE_RESTITUTION : 0;
    g.vel.x = _tan.x * keep + tr.normal.x * bounce;
    g.vel.y = _tan.y * keep + tr.normal.y * bounce;
    g.vel.z = _tan.z * keep + tr.normal.z * bounce;

    // A bounce scrubs spin as well — a tumbling gun that hits concrete stops tumbling.
    g.spin.x *= keep;
    g.spin.y *= keep;
    g.spin.z *= keep;

    if (tr.normal.y >= GUN_FLOOR_COS) restedOnFloor = true;
    remaining *= 1 - f;
  }

  // Visual tumble.
  g.rot.x = (g.rot.x + g.spin.x * TICK_DT) % TAU;
  g.rot.y = (g.rot.y + g.spin.y * TICK_DT) % TAU;
  g.rot.z = (g.rot.z + g.spin.z * TICK_DT) % TAU;

  if (g.pos.y < world.arena.killPlaneY) {
    enterVoid(g);
    return;
  }

  // Settle. A chambered round does NOT survive hitting the floor: an empty gun is dead
  // grey and silent, and the whole readability contract (DESIGN.md §4) depends on the
  // glow being honest about what a pickup will give you.
  if (restedOnFloor && length(g.vel) < GUN_REST_SPEED) {
    g.state = GunState.Grounded;
    g.chambered = false;
    g.throwOrigin = null;
    set(g.vel, 0, 0, 0);
    set(g.spin, 0, 0, 0);
    g.rot.x = 0;
    g.rot.z = 0;
    return;
  }

  if (g.idleMs >= GUN_ABANDONED_RESET_MS) resetToSpawn(g, world, events, 'abandoned');
}

function stepGrounded(g: GunEntity, w: WorldState, world: CollisionWorld, events: GameEvent[]): void {
  g.holder = null;
  g.chambered = false;
  set(g.vel, 0, 0, 0);
  set(g.spin, 0, 0, 0);
  if (g.throwLockoutMs > 0) g.throwLockoutMs = Math.max(0, g.throwLockoutMs - TICK_MS);
  g.idleMs += TICK_MS;

  if (g.pos.y < world.arena.killPlaneY) {
    enterVoid(g);
    return;
  }
  if (g.idleMs >= GUN_ABANDONED_RESET_MS) resetToSpawn(g, world, events, 'abandoned');
}

function stepVoid(g: GunEntity, world: CollisionWorld, events: GameEvent[]): void {
  g.voidRespawnMs = Math.max(0, g.voidRespawnMs - TICK_MS);
  if (g.voidRespawnMs > 0) return;
  resetToSpawn(g, world, events, 'void');
}

function enterVoid(g: GunEntity): void {
  g.state = GunState.Void;
  g.holder = null;
  g.chambered = false;
  g.lastThrower = null;
  g.throwLockoutMs = 0;
  g.idleMs = 0;
  g.airborneMs = 0;
  g.throwOrigin = null;
  g.voidRespawnMs = GUN_VOID_RESPAWN_MS;
  set(g.vel, 0, 0, 0);
  set(g.spin, 0, 0, 0);
}

/**
 * Put the gun back in play at a fair spawn. It arrives in flight with no velocity so it
 * falls in and settles naturally — and so an alert player can meet it in the air and
 * chamber it. A round can never deadlock on a lost gun.
 */
function resetToSpawn(
  g: GunEntity,
  world: CollisionWorld,
  events: GameEvent[],
  reason: 'void' | 'abandoned',
): void {
  fairSpawn(g, world, _spawn);
  copy(g.pos, _spawn);
  set(g.vel, 0, 0, 0);
  set(g.spin, 0, 0, 0);
  set(g.rot, 0, 0, 0);
  g.state = GunState.InFlight;
  g.holder = null;
  g.chambered = false;
  g.lastThrower = null;
  g.throwLockoutMs = 0;
  g.idleMs = 0;
  g.airborneMs = 0;
  g.throwOrigin = null;
  g.voidRespawnMs = 0;
  events.push({ t: 'gunreset', gunId: g.id, reason });
}

// ---------------------------------------------------------------------------
// resolveCatches — THE CRUX
// ---------------------------------------------------------------------------

/**
 * Every in-flight gun looks for a pair of hands.
 *
 * A catch is legal when ALL of these hold (DESIGN.md §1):
 *   - the catcher is alive and empty-handed;
 *   - the gun is inside CATCH_RADIUS of their hands;
 *   - the gun is at least CATCH_MIN_HEIGHT off the floor — a catch, not a scoop;
 *   - the gun is inside CATCH_VIEW_CONE of their view direction. THE CATCH IS AN ACTION:
 *     you cannot catch what you are not looking at, which is what makes a pass cost both
 *     players their aim at the same moment;
 *   - line of sight is clear (CATCH_REQUIRE_LOS) — no catching through cover;
 *   - the catcher is not the thrower inside their own self-catch lockout.
 *
 * Catching your own throw after the lockout is legal but yields an EMPTY gun — see the
 * note in `doCatch`. Self-chambering is impossible by identity, not merely by timing.
 *
 * Resolution follows the DESIGN.md §1 table exactly:
 *   healthy               -> gun chambers, catcher is armed
 *   wounded               -> catcher healed to full, chamber spent, catcher holds an empty gun
 *   wounded + CatchRaw    -> catcher stays wounded, gun chambers   (skill play)
 *
 * Also advances the per-player weapon timers, because this is the one gun function that
 * is guaranteed to run exactly once per tick.
 */
const _look = v3();
const _toGun = v3();
const _pathA = v3();
const _catchPoint = v3();

export function resolveCatches(w: WorldState, events: GameEvent[]): void {
  for (const id in w.players) {
    const p = w.players[id];
    if (p) advanceWeaponTimers(p, w);
  }

  const world = worldsFor.get(w);

  for (let gi = 0; gi < w.guns.length; gi++) {
    const g = w.guns[gi];
    if (!g || g.state !== GunState.InFlight) continue;

    // Height test. With a collision world we probe the actual floor beneath the gun; if
    // one has not been handed to us yet we fall back to height above the catcher's feet.
    let airborne = true;
    let needFootCheck = false;
    if (world) airborne = !world.raycast(g.pos, _down, CATCH_MIN_HEIGHT, _probe).hit;
    else needFootCheck = true;
    if (!airborne) continue;

    // The path the gun swept this tick. Catching is tested against the whole segment, not
    // just the end point: a charged pass covers over a metre per tick and must never clip
    // through a receiver's hands between two samples.
    addScaled(_pathA, g.pos, g.vel, -TICK_DT);

    let best: PlayerState | null = null;
    let bestDistSq = CATCH_RADIUS * CATCH_RADIUS;

    for (const id in w.players) {
      const p = w.players[id];
      if (!p) continue;
      if (p.healthState === HealthState.Dead) continue;
      if (p.heldGun >= 0) continue; // hands full
      if (p.id === g.lastThrower && g.throwLockoutMs > 0) continue; // no self-chambering
      if (needFootCheck && g.pos.y - p.pos.y < CATCH_MIN_HEIGHT) continue;

      // One anchor for everything: the eye. Hands, view cone and line of sight all measure
      // from the same point, so a pass can never be simultaneously "too far from your
      // hands" and "too close to your face to be in view".
      eyePosition(p, _eye);
      const dSq = closestOnSegment(_eye, _pathA, g.pos, _catchPoint);
      if (dSq > bestDistSq) continue;
      // Deterministic tie-break so server and client never disagree on who caught it.
      if (best && Math.abs(dSq - bestDistSq) < EPSILON && p.id > best.id) continue;
      if (!isLookingAt(p, _catchPoint, world)) continue;
      best = p;
      bestDistSq = dSq;
    }

    if (best) doCatch(best, g, w, events);
  }
}

/**
 * Squared distance from `pt` to segment [a,b], with the closest point written to `out`.
 */
function closestOnSegment(pt: Vec3, a: Vec3, b: Vec3, out: Vec3): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const abz = b.z - a.z;
  const lenSq = abx * abx + aby * aby + abz * abz;
  let t = 0;
  if (lenSq > EPSILON) {
    t = clamp01(((pt.x - a.x) * abx + (pt.y - a.y) * aby + (pt.z - a.z) * abz) / lenSq);
  }
  out.x = a.x + abx * t;
  out.y = a.y + aby * t;
  out.z = a.z + abz * t;
  const dx = pt.x - out.x;
  const dy = pt.y - out.y;
  const dz = pt.z - out.z;
  return dx * dx + dy * dy + dz * dz;
}

/**
 * THE CATCH IS AN ACTION. The gun must be inside the catcher's view cone with clear line
 * of sight — you cannot catch what is behind you and you cannot catch through a wall.
 * This is the cost of every pass: for that moment neither player is aiming at an enemy.
 *
 * `at` is the point on the gun's swept path nearest the catcher's eye; `_eye` must already
 * hold that player's eye position.
 */
function isLookingAt(p: PlayerState, at: Vec3, world: CollisionWorld | undefined): boolean {
  set(_toGun, at.x - _eye.x, at.y - _eye.y, at.z - _eye.z);
  const dist = length(_toGun);
  // Already inside the hands — no meaningful direction to test, and it is plainly in view.
  if (dist < GUN_RADIUS) return true;

  const inv = 1 / dist;
  _toGun.x *= inv;
  _toGun.y *= inv;
  _toGun.z *= inv;

  dirFromYawPitch(_look, p.yaw, p.pitch);
  if (dot(_look, _toGun) < CATCH_VIEW_COS) return false;

  if (CATCH_REQUIRE_LOS && world) {
    // Stop a hair short of the gun so a gun resting against a wall is still catchable.
    const reach = Math.max(0, dist - GUN_RADIUS);
    if (reach > EPSILON && world.raycast(_eye, _toGun, reach, _probe).hit) return false;
  }
  return true;
}

function doCatch(p: PlayerState, g: GunEntity, w: WorldState, events: GameEvent[]): void {
  const thrower = g.lastThrower ? w.players[g.lastThrower] : undefined;
  const intercept = thrower ? thrower.team !== p.team : g.team !== p.team;

  const raw = heldNow(p, Btn.CatchRaw);
  const wounded = p.healthState === HealthState.Wounded;

  /**
   * YOU CANNOT SELF-CHAMBER (DESIGN.md §1). THROW_SELF_CATCH_LOCKOUT_MS stops the instant
   * juggle, but it cannot stop a patient one: a straight-up lob at THROW_SPEED hangs for
   * over two seconds, far longer than the lockout, so a lone player could stand under
   * their own toss and re-arm forever. So the rule is enforced by *identity*, not only by
   * time — the slide is racked by the pass, and a gun that comes back to the same hands it
   * left was never passed. Catching your own throw (or your own disarmed gun) is legal and
   * feels good; it just hands you a paperweight. You need your team.
   */
  const selfCatch = g.lastThrower !== null && g.lastThrower === p.id;

  let chambered = CATCH_CHAMBERS && !selfCatch;
  if (!selfCatch && wounded && CATCH_HEALS_WOUNDED && !raw) {
    // Heal the body, spend the round. Your team is now whole and disarmed.
    reviveToFull(p);
    chambered = false;
    events.push({ t: 'heal', player: p.id, gunId: g.id });
  }

  g.chambered = chambered;
  g.state = chambered ? GunState.HeldChambered : GunState.HeldEmpty;
  g.holder = p.id;
  g.team = p.team; // guns re-tint to whoever holds them
  g.lastThrower = null;
  g.throwLockoutMs = 0;
  g.idleMs = 0;
  g.airborneMs = 0;
  g.throwOrigin = null;
  g.voidRespawnMs = 0;
  set(g.vel, 0, 0, 0);
  set(g.spin, 0, 0, 0);

  p.heldGun = g.id;
  p.catchLockoutMs = CATCH_FIRE_DELAY_MS;
  p.throwChargeMs = 0;
  // `catches` is the stat that measures teamwork, so plucking your own throw out of the
  // air does not count toward it.
  if (!selfCatch) p.catches++;
  if (intercept) p.intercepts++;

  events.push({
    t: 'catch',
    player: p.id,
    gunId: g.id,
    chambered,
    raw: raw && wounded && !selfCatch,
    intercept,
  });
}

// ---------------------------------------------------------------------------
// tryFire
// ---------------------------------------------------------------------------

const _origin = v3();
const _aim = v3();

/**
 * Lag-compensation rewind for this shot.
 *
 * `cmd.clientTimeMs` is only usable when the caller has already rebased it onto server
 * time; when it plausibly is (it lands inside a sane window behind the server clock) we
 * trust it, otherwise we fall back to the classic latency + interpolation-delay estimate.
 * Either way it is clamped inside traceShot.
 */
function computeRewindMs(p: PlayerState, cmd: InputCommand, w: WorldState): number {
  const fromClock = w.timeMs - cmd.clientTimeMs;
  if (fromClock >= 0 && fromClock <= MAX_REWIND_MS * 2) return clamp(fromClock, 0, MAX_REWIND_MS);
  return clamp(p.latencyMs + INTERP_DELAY_MS, 0, MAX_REWIND_MS);
}

/**
 * Semi-auto, one round at a time.
 *
 * If the chamber is empty this emits 'dryfire' — the dry click. It is deliberately not
 * suppressed, not queued, and not silently swallowed: pulling the trigger on an empty gun
 * is how this game tells you that you are already dead.
 */
export function tryFire(
  p: PlayerState,
  cmd: InputCommand,
  w: WorldState,
  world: CollisionWorld,
  events: GameEvent[],
): void {
  noteInput(p, cmd, w);
  advanceWeaponTimers(p, w);
  worldsFor.set(w, world);

  if (!pressedEdge(p, Btn.Fire)) return;
  if (!canAct(w, p)) return;

  const g = heldGunOf(p, w);
  if (!g) return;
  if (p.fireCooldownMs > 0 || p.catchLockoutMs > 0) return;

  if (!g.chambered) {
    p.fireCooldownMs = FIRE_COOLDOWN_MS;
    events.push({ t: 'dryfire', player: p.id });
    return;
  }

  eyePosition(p, _origin);
  dirFromYawPitch(_aim, cmd.yaw, cmd.pitch);

  const hit = traceShot(_origin, _aim, p.id, w, world, computeRewindMs(p, cmd, w));

  // The round is spent the instant the trigger breaks — hit, miss, or teammate.
  g.chambered = false;
  g.state = GunState.HeldEmpty;
  p.fireCooldownMs = FIRE_COOLDOWN_MS;
  p.throwChargeMs = 0;

  events.push({
    t: 'shot',
    player: p.id,
    origin: { x: _origin.x, y: _origin.y, z: _origin.z },
    dir: { x: _aim.x, y: _aim.y, z: _aim.z },
    hitPoint: { x: hit.point.x, y: hit.point.y, z: hit.point.z },
    hitPlayer: hit.playerId,
    zone: hit.zone,
  });

  if (hit.playerId) applyDamage(hit.playerId, p.id, hit.zone, g.id, w, events);
}

// ---------------------------------------------------------------------------
// tryThrow
// ---------------------------------------------------------------------------

const _throwDir = v3();
const _throwVel = v3();
const _spinAxis = v3();

/**
 * Hold to charge, release to pass. The throw is the only way to arm a teammate, so it has
 * to be readable: charge scales speed between THROW_SPEED and THROW_SPEED_CHARGED, the arc
 * gets a slight upward bias so passes clear heads, and it inherits part of the thrower's
 * velocity so a pass thrown while sliding actually leads the receiver.
 */
export function tryThrow(p: PlayerState, cmd: InputCommand, w: WorldState, events: GameEvent[]): void {
  noteInput(p, cmd, w);
  advanceWeaponTimers(p, w);

  const g = heldGunOf(p, w);
  if (!g || !canAct(w, p)) {
    p.throwChargeMs = 0;
    return;
  }

  if (hasBtn(cmd.buttons, Btn.Throw)) {
    p.throwChargeMs = Math.min(THROW_CHARGE_MS, p.throwChargeMs + TICK_MS);
    return;
  }

  if (p.throwChargeMs <= 0) return;

  const charge01 = clamp01(p.throwChargeMs / THROW_CHARGE_MS);
  const speed = lerpN(THROW_SPEED, THROW_SPEED_CHARGED, charge01);
  const fullyCharged = p.throwChargeMs >= THROW_CHARGE_MS;
  p.throwChargeMs = 0;

  dirFromYawPitch(_throwDir, cmd.yaw, cmd.pitch);
  _throwDir.y += THROW_UP_BIAS;
  normalize(_throwDir, _throwDir);

  // Leave the hands clear of the thrower's own capsule so it can't instantly re-collide.
  eyePosition(p, _origin);
  addScaled(g.pos, _origin, _throwDir, PLAYER_RADIUS + GUN_RADIUS);

  set(
    _throwVel,
    _throwDir.x * speed + p.vel.x * THROW_INHERIT_VELOCITY,
    _throwDir.y * speed + p.vel.y * THROW_INHERIT_VELOCITY,
    _throwDir.z * speed + p.vel.z * THROW_INHERIT_VELOCITY,
  );

  launch(g, p, _throwVel, THROW_SELF_CATCH_LOCKOUT_MS);

  // End-over-end tumble about the axis perpendicular to the throw. Deterministic: the sim
  // may never call Math.random().
  rightFromYaw(_spinAxis, cmd.yaw);
  set(g.spin, _spinAxis.x * THROW_SPIN_RATE, 0, _spinAxis.z * THROW_SPIN_RATE);

  events.push({ t: 'throw', player: p.id, gunId: g.id, charged: fullyCharged });
}

// ---------------------------------------------------------------------------
// tryPickup
// ---------------------------------------------------------------------------

/**
 * Ground pickup. Always yields an EMPTY gun — PICKUP_FROM_GROUND_CHAMBERS is false and
 * must stay false. This is the rule that turns every miss into a scramble.
 */
export function tryPickup(p: PlayerState, w: WorldState, events: GameEvent[]): void {
  advanceWeaponTimers(p, w);
  if (p.healthState === HealthState.Dead) return;
  if (p.heldGun >= 0) return;

  const reachSq = CATCH_RADIUS * CATCH_RADIUS;
  const height = p.crouching ? PLAYER_CROUCH_HEIGHT : PLAYER_HEIGHT;

  let best: GunEntity | null = null;
  let bestDistSq = reachSq;

  for (let i = 0; i < w.guns.length; i++) {
    const g = w.guns[i];
    if (!g || g.state !== GunState.Grounded) continue;
    const dx = g.pos.x - p.pos.x;
    const dz = g.pos.z - p.pos.z;
    const dy = g.pos.y - p.pos.y;
    if (dy < -height || dy > height) continue;
    const dSq = dx * dx + dz * dz;
    if (dSq > bestDistSq) continue;
    if (best && Math.abs(dSq - bestDistSq) < EPSILON && g.id > best.id) continue;
    best = g;
    bestDistSq = dSq;
  }

  if (!best) return;

  best.state = GunState.HeldEmpty;
  best.chambered = PICKUP_FROM_GROUND_CHAMBERS;
  best.holder = p.id;
  best.team = p.team; // re-tints to the new owner
  best.lastThrower = null;
  best.throwLockoutMs = 0;
  best.idleMs = 0;
  best.airborneMs = 0;
  best.throwOrigin = null;
  best.voidRespawnMs = 0;
  set(best.vel, 0, 0, 0);
  set(best.spin, 0, 0, 0);

  p.heldGun = best.id;
  p.throwChargeMs = 0;

  events.push({ t: 'pickup', player: p.id, gunId: best.id });
}

// ---------------------------------------------------------------------------
// tryPulse
// ---------------------------------------------------------------------------

const _pulseDir = v3();
const _toTarget = v3();
const _push = v3();

/**
 * PULSE — the unarmed player's tool, and the highest-value play available to them.
 *
 * A short kinetic cone that shoves players, knocks a gun off course mid-pass, and rips a
 * gun out of an enemy's hands. Armed players get a weaker version (PULSE_ARMED_MULT) so
 * holding the gun is a trade-off rather than a strict upgrade.
 *
 * Teammates get shoved too — a boost is a legitimate tool — but only enemies get disarmed.
 */
export function tryPulse(p: PlayerState, cmd: InputCommand, w: WorldState, events: GameEvent[]): void {
  noteInput(p, cmd, w);
  advanceWeaponTimers(p, w);

  if (!pressedEdge(p, Btn.Pulse)) return;
  if (!canAct(w, p)) return;
  if (p.pulseCooldownMs > 0) return;

  p.pulseCooldownMs = PULSE_COOLDOWN_MS;

  const armed = heldGunOf(p, w) !== null;
  const mult = armed ? PULSE_ARMED_MULT : 1;

  eyePosition(p, _origin);
  dirFromYawPitch(_pulseDir, cmd.yaw, cmd.pitch);

  const hitPlayers: string[] = [];
  let hitGun = -1;

  // --- players -------------------------------------------------------------
  for (const id in w.players) {
    const target = w.players[id];
    if (!target || target.id === p.id) continue;
    if (target.healthState === HealthState.Dead) continue;

    const th = target.crouching ? PLAYER_CROUCH_HEIGHT : PLAYER_HEIGHT;
    set(_toTarget, target.pos.x - _origin.x, target.pos.y + th * 0.5 - _origin.y, target.pos.z - _origin.z);
    const dist = length(_toTarget);
    if (dist > PULSE_RANGE + PLAYER_RADIUS) continue;
    if (dist < EPSILON) {
      copy(_push, _pulseDir);
    } else {
      const inv = 1 / dist;
      set(_push, _toTarget.x * inv, _toTarget.y * inv, _toTarget.z * inv);
      if (dot(_push, _pulseDir) < PULSE_CONE_COS) continue;
    }

    _push.y += THROW_UP_BIAS; // pop them off the floor so the shove actually moves them
    normalize(_push, _push);

    const impulse = PULSE_PLAYER_IMPULSE * mult;
    addScaled(target.vel, target.vel, _push, impulse);
    target.grounded = false;
    hitPlayers.push(target.id);

    // Disarm: an enemy's gun is torn loose and thrown along the pulse.
    if (PULSE_DISARMS && target.team !== p.team) {
      const g = heldGunOf(target, w);
      if (g) {
        set(
          _throwVel,
          _push.x * PULSE_GUN_IMPULSE * mult + target.vel.x * THROW_INHERIT_VELOCITY,
          _push.y * PULSE_GUN_IMPULSE * mult + target.vel.y * THROW_INHERIT_VELOCITY,
          _push.z * PULSE_GUN_IMPULSE * mult + target.vel.z * THROW_INHERIT_VELOCITY,
        );
        launch(g, target, _throwVel, THROW_SELF_CATCH_LOCKOUT_MS);
        target.throwChargeMs = 0;
        set(g.spin, 0, THROW_SPIN_RATE, 0);
        if (hitGun < 0) hitGun = g.id;
        events.push({ t: 'disarm', player: target.id, gunId: g.id, by: p.id });
      }
    }
  }

  // --- loose guns ----------------------------------------------------------
  for (let i = 0; i < w.guns.length; i++) {
    const g = w.guns[i];
    if (!g) continue;
    if (g.state !== GunState.InFlight && g.state !== GunState.Grounded) continue;

    set(_toTarget, g.pos.x - _origin.x, g.pos.y - _origin.y, g.pos.z - _origin.z);
    const dist = length(_toTarget);
    if (dist > PULSE_RANGE + GUN_RADIUS) continue;
    if (dist < EPSILON) copy(_push, _pulseDir);
    else {
      const inv = 1 / dist;
      set(_push, _toTarget.x * inv, _toTarget.y * inv, _toTarget.z * inv);
      if (dot(_push, _pulseDir) < PULSE_CONE_COS) continue;
    }

    _push.y += THROW_UP_BIAS;
    normalize(_push, _push);

    const impulse = PULSE_GUN_IMPULSE * mult;
    if (g.state === GunState.Grounded) {
      // Kicking a grounded gun puts it back in the air: denial, or a bank pass to a mate.
      set(_throwVel, _push.x * impulse, _push.y * impulse, _push.z * impulse);
      launch(g, null, _throwVel, THROW_SELF_CATCH_LOCKOUT_MS);
      g.lastThrower = p.id;
      set(g.spin, 0, THROW_SPIN_RATE, 0);
    } else {
      addScaled(g.vel, g.vel, _push, impulse);
      g.idleMs = 0;
    }
    if (hitGun < 0) hitGun = g.id;
  }

  events.push({
    t: 'pulse',
    player: p.id,
    pos: { x: _origin.x, y: _origin.y, z: _origin.z },
    hitPlayers,
    hitGun,
  });
}
